import type {
  AeroCampaignAxis,
  AeroCampaignPlanResponse,
  AeroSectionSample,
  AeroSectionSolveResponse,
  AeroSectionSolveRow,
  AeroSolvePoint,
} from "../../api/aeroWorkbench";
import type { WingDefinition } from "./api";
import { planformShape, type SectionCampaignControls } from "./sectionCampaignModel";

export const SECTION_PILOT_LIMIT = 64;

export interface CampaignCoverageGroup {
  id: string;
  designIndex: number;
  pointIds: string[];
  rowCount: number;
  datasetSplit: string;
}

function orderedBounds(a: number, b: number, epsilon: number): [number, number] {
  const lower = Math.min(a, b);
  const upper = Math.max(a, b);
  return upper - lower >= epsilon ? [lower, upper] : [lower, lower + epsilon];
}

function centredBounds(value: number, radius: number, floor?: number, ceiling?: number): [number, number] {
  let lower = value - radius;
  let upper = value + radius;
  if (floor != null) lower = Math.max(floor, lower);
  if (ceiling != null) upper = Math.min(ceiling, upper);
  if (upper <= lower) upper = lower + Math.max(1e-6, radius);
  return [lower, upper];
}

function boundedOrdered(
  a: number,
  b: number,
  floor: number,
  ceiling: number,
  epsilon: number,
): [number, number] {
  let lower = Math.max(floor, Math.min(ceiling, Math.min(a, b)));
  let upper = Math.max(floor, Math.min(ceiling, Math.max(a, b)));
  if (upper - lower < epsilon) {
    if (upper + epsilon <= ceiling) upper += epsilon;
    else lower = Math.max(floor, lower - epsilon);
  }
  return [lower, upper];
}

function axis(
  name: string,
  role: AeroCampaignAxis["role"],
  bounds: [number, number],
  options: Partial<Pick<
    AeroCampaignAxis,
    "transform" | "semantic" | "smoothness" | "nonlinearity" | "expected_correlation_length"
  >> = {},
): AeroCampaignAxis {
  return {
    name,
    role,
    lower: bounds[0],
    upper: bounds[1],
    transform: options.transform ?? "linear",
    semantic: options.semantic ?? "generic",
    smoothness: options.smoothness ?? "medium",
    nonlinearity: options.nonlinearity ?? "medium",
    ...(options.expected_correlation_length == null
      ? {}
      : { expected_correlation_length: options.expected_correlation_length }),
  };
}

/** Translate the workbench knobs into the API's explicit 2D-vs-3D axis contract. */
export function campaignAxesForWing(
  wing: WingDefinition,
  controls: SectionCampaignControls,
): AeroCampaignAxis[] {
  const planform = planformShape(wing);
  const camberBounds = boundedOrdered(controls.camberMinPercent / 100, controls.camberMaxPercent / 100, 0, 0.09, 0.001);
  const camberPositionBounds = boundedOrdered(controls.camberPositionMinXc, controls.camberPositionMaxXc, 0.01, 0.99, 0.01);
  const thicknessBounds = boundedOrdered(controls.thicknessMinPercent / 100, controls.thicknessMaxPercent / 100, 0.01, 0.4, 0.005);
  const [alphaLower, alphaUpper] = orderedBounds(controls.alphaMin, controls.alphaMax, 0.1);
  const [reLower, reUpper] = orderedBounds(controls.reynoldsMin, controls.reynoldsMax, 1);
  const [machLower, machUpper] = orderedBounds(controls.machMin, controls.machMax, 1e-3);

  return [
    axis("camber_ratio", "section_shape", camberBounds, { expected_correlation_length: 0.01 }),
    axis("camber_position_xc", "section_shape", camberPositionBounds, { expected_correlation_length: 0.1 }),
    axis("thickness_ratio", "section_shape", thicknessBounds, { expected_correlation_length: 0.02 }),
    axis("alpha", "operating_condition", [alphaLower, alphaUpper], {
      semantic: "alpha",
      smoothness: "low",
      nonlinearity: "high",
      expected_correlation_length: 2,
    }),
    axis("reynolds", "operating_condition", [Math.max(1, reLower), Math.max(2, reUpper)], {
      transform: "log10",
      semantic: "reynolds",
      expected_correlation_length: 0.5,
    }),
    axis("mach", "operating_condition", [Math.max(0, machLower), Math.max(1e-3, machUpper)], {
      semantic: "mach",
      expected_correlation_length: 0.05,
    }),
    // These affect the later 3D lifting-line sweep but do not consume 2D rows.
    axis("half_span", "lifting_line_geometry", centredBounds(planform.halfSpan, Math.max(0.1, planform.halfSpan * 0.2), 0.1)),
    axis("root_chord", "lifting_line_geometry", centredBounds(planform.rootChord, Math.max(0.05, planform.rootChord * 0.2), 0.05)),
    axis("tip_chord", "lifting_line_geometry", centredBounds(planform.tipChord, Math.max(0.05, planform.tipChord * 0.2), 0.05)),
    axis("sweep_deg", "lifting_line_geometry", centredBounds(planform.sweepDeg, 8)),
    axis("dihedral_deg", "lifting_line_geometry", centredBounds(planform.dihedralDeg, 4)),
    axis("tip_twist_deg", "lifting_line_geometry", centredBounds(planform.tipTwistDeg, 3)),
  ];
}

function coordinates(point: AeroSolvePoint): Record<string, number> {
  return Object.fromEntries(point.coordinates);
}

function coverageSplit(points: readonly AeroSolvePoint[], declared?: string): string {
  const pointSplits = new Set(points.map((point) => point.dataset_split).filter((split): split is string => Boolean(split)));
  if (declared && [...pointSplits].some((split) => split !== declared)) return "mixed";
  if (declared) return declared;
  if (pointSplits.size === 1) return [...pointSplits][0]!;
  return pointSplits.size > 1 ? "mixed" : "unassigned";
}

/** Build every authoritative alpha-sweep/dataset group used by the server plan. */
export function campaignCoverageGroups(response: AeroCampaignPlanResponse): CampaignCoverageGroup[] {
  const pointById = new Map(response.plan.solve_points.map((point) => [point.point_id, point]));
  if (response.plan.alpha_sweeps.length > 0) {
    return response.plan.alpha_sweeps.map((sweep, designIndex) => {
      const points = sweep.solve_point_ids.map((pointId) => pointById.get(pointId)).filter((point): point is AeroSolvePoint => Boolean(point));
      return {
        id: sweep.sweep_id,
        designIndex,
        pointIds: points.map((point) => point.point_id),
        rowCount: points.length,
        datasetSplit: coverageSplit(points, sweep.dataset_split),
      };
    });
  }

  const grouped = new Map<string, AeroSolvePoint[]>();
  for (const point of response.plan.solve_points) {
    const points = grouped.get(point.alpha_sweep_id) ?? [];
    points.push(point);
    grouped.set(point.alpha_sweep_id, points);
  }
  if (grouped.size === response.plan.actual_alpha_sweep_count) {
    return [...grouped.entries()].map(([id, points], designIndex) => ({
      id,
      designIndex,
      pointIds: points.map((point) => point.point_id),
      rowCount: points.length,
      datasetSplit: coverageSplit(points),
    }));
  }

  // Compatibility for early/stub planners that omit alpha_sweeps or reuse
  // placeholder sweep ids: distribute every solve point across the declared
  // group count so the UI never falls back to its local 60-group sketch.
  const groupCount = Math.max(1, response.plan.actual_alpha_sweep_count);
  return Array.from({ length: groupCount }, (_, designIndex) => {
    const start = Math.floor(designIndex * response.plan.solve_points.length / groupCount);
    const end = Math.floor((designIndex + 1) * response.plan.solve_points.length / groupCount);
    const points = response.plan.solve_points.slice(start, end);
    return {
      id: `alpha-sweep-${designIndex + 1}`,
      designIndex,
      pointIds: points.map((point) => point.point_id),
      rowCount: points.length,
      datasetSplit: coverageSplit(points),
    };
  });
}

function splitForPoint(response: AeroCampaignPlanResponse, point: AeroSolvePoint): string {
  const sweepSplit = response.plan.alpha_sweeps.find((sweep) => sweep.sweep_id === point.alpha_sweep_id)?.dataset_split;
  if (sweepSplit && point.dataset_split && sweepSplit !== point.dataset_split) {
    throw new Error(
      `campaign point ${point.point_id} split ${point.dataset_split} conflicts with alpha sweep split ${sweepSplit}`,
    );
  }
  return sweepSplit ?? point.dataset_split ?? "unassigned";
}

function sampleFromPoint(response: AeroCampaignPlanResponse, point: AeroSolvePoint): AeroSectionSample {
  const values = coordinates(point);
  const required = ["camber_ratio", "camber_position_xc", "thickness_ratio", "alpha", "reynolds", "mach"] as const;
  for (const name of required) {
    if (!Number.isFinite(values[name])) throw new Error(`campaign point ${point.point_id} is missing ${name}`);
  }
  return {
    point_id: point.point_id,
    camber_ratio: values.camber_ratio!,
    camber_position_xc: values.camber_position_xc!,
    thickness_ratio: values.thickness_ratio!,
    alpha_deg: values.alpha!,
    reynolds: values.reynolds!,
    mach: values.mach!,
    dataset_split: splitForPoint(response, point),
  };
}

/** Materialize the bounded first pilot batch; later rows remain in the saved plan. */
export function pilotSamplesFromPlan(
  response: AeroCampaignPlanResponse,
  requestedLimit = SECTION_PILOT_LIMIT,
  offset = 0,
): AeroSectionSample[] {
  const limit = Math.max(1, Math.min(SECTION_PILOT_LIMIT, Math.floor(requestedLimit)));
  const start = Math.max(0, Math.floor(offset));
  return response.plan.solve_points.slice(start, start + limit).map((point) => sampleFromPoint(response, point));
}

/** Materialize an ordered retry queue without allowing a duplicate request row. */
export function samplesForPointIds(
  response: AeroCampaignPlanResponse,
  pointIds: readonly string[],
  requestedLimit = SECTION_PILOT_LIMIT,
): AeroSectionSample[] {
  const limit = Math.max(1, Math.min(SECTION_PILOT_LIMIT, Math.floor(requestedLimit)));
  const pointsById = new Map(response.plan.solve_points.map((point) => [point.point_id, point]));
  const uniqueIds = [...new Set(pointIds)].slice(0, limit);
  return uniqueIds.map((pointId) => {
    const point = pointsById.get(pointId);
    if (!point) throw new Error(`campaign retry point ${pointId} is not present in the saved plan`);
    return sampleFromPoint(response, point);
  });
}

export function isRetryableRow(row: AeroSectionSolveRow): boolean {
  return row.status === "error" && (row.retryable === true || row.error_kind === "transport");
}

/** Keep one latest row per physical point while retaining cumulative execution counters. */
export function mergeSolveResponses(
  previous: AeroSectionSolveResponse | null,
  next: AeroSectionSolveResponse,
): AeroSectionSolveResponse {
  const rowsById = new Map<string, AeroSectionSolveRow>();
  for (const row of previous?.rows ?? []) rowsById.set(row.point_id, row);
  for (const row of next.rows) rowsById.set(row.point_id, row);
  const rows = [...rowsById.values()];
  const completed = rows.filter((row) => row.status === "done").length;
  const previousMemory = previous?.foil_memory;
  const nextMemory = next.foil_memory;
  const mergedMemory = nextMemory || previousMemory ? {
    ...(previousMemory ?? nextMemory!),
    ...(nextMemory ?? {}),
    returned_scopes: [...new Set([
      ...(previousMemory?.returned_scopes ?? []),
      ...(nextMemory?.returned_scopes ?? []),
    ])],
    shared: previousMemory?.shared === true || nextMemory?.shared === true,
    exact_hits: (previousMemory?.exact_hits ?? 0) + (nextMemory?.exact_hits ?? 0),
    execution_cache_hits: (previousMemory?.execution_cache_hits ?? 0) + (nextMemory?.execution_cache_hits ?? 0),
    persisted_observations: (previousMemory?.persisted_observations ?? 0) + (nextMemory?.persisted_observations ?? 0),
    publication_failures: (previousMemory?.publication_failures ?? 0) + (nextMemory?.publication_failures ?? 0),
    solver_runs: (previousMemory?.solver_runs ?? 0) + (nextMemory?.solver_runs ?? 0),
    approximate_predictions: previousMemory?.approximate_predictions === true || nextMemory?.approximate_predictions === true,
    approximate_hits: (previousMemory?.approximate_hits ?? 0) + (nextMemory?.approximate_hits ?? 0),
  } : undefined;
  return {
    ...next,
    requested: rows.length,
    completed,
    failed: rows.length - completed,
    cache_hits: rows.filter((row) => row.status === "done" && row.cached === true).length,
    solver_runs: (previous?.solver_runs ?? 0) + (next.solver_runs ?? 0),
    foil_memory: mergedMemory,
    rows,
  };
}
